import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Button, Card, Flag, IconButton, Separator} from 'platform/components';
import {useFormatNumber} from 'platform/locale';
import styled from 'styled-components';

import {useCallback, useMemo, useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNil, isNotNil, join, sortBy} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useFilter} from '../../hooks/FilterContext';
import {useCatalogue} from '../../hooks/useCatalogue';
import {useTrimLevelsCatalog} from '../../hooks/useTrimLevelsCatalog';
import i18n from '../../i18n/i18n';
import {useReorderRelativeFiltersMutation} from '../../store/omneticApi';
import {RelativeFilterResponseBody} from '../../types/api/api';
import {FilterFormData} from '../../types/FilterFormData';
import {PriceReportType} from '../../types/PriceReportType';
import {handleApiError} from '../../utils/handleApiError';
import {PresetEditorDialog} from '../Filter/components/SavedFilters/components/PresetEditorDialog';
import {usePresetEditor} from '../Filter/components/SavedFilters/hooks/usePresetEditor';
import {RelativeFilterPresetsController} from '../Filter/components/SavedFilters/hooks/useRelativeFilterPresetsController';
import {buildRelativeConfigFromFormValues} from '../Filter/components/SavedFilters/utils/buildRelativeConfigFromFormValues';
import {buildReorderRelativeFiltersBody} from '../Filter/components/SavedFilters/utils/buildReorderRelativeFiltersBody';
import {getPresetDisplayName} from '../Filter/components/SavedFilters/utils/getPresetDisplayName';
import {PanelGrid, PanelGridFullWidth} from './components/PanelGrid';
import {PresetMetricsCells} from './components/PresetMetricsCells';
import {PresetMetricsRow} from './components/PresetMetricsRow';
import {useCurrentFilterMetrics} from './hooks/useCurrentFilterMetrics';
import {useFormatPanelPrice} from './hooks/useFormatPanelPrice';
import {buildFilterSummary, FilterSummaryHelpers} from './utils/buildFilterSummary';

interface SavedFiltersPanelProps extends TestIdProps {
  presetController: RelativeFilterPresetsController;
  priceReport: PriceReportType;
  formApi: UseFormReturn<FilterFormData>;
}

/**
 * Full-width strip under the header showing market metrics for the currently applied
 * filters. Titled by the selected saved filter, or “Custom filter” when the filters
 * match no saved preset. Expands into a comparison list of all saved filters, each
 * row lazily fetching its own market statistics.
 */
export function SavedFiltersPanel(props: SavedFiltersPanelProps) {
  const {presetController, priceReport, formApi} = props;
  const {getCarStyle, getFuelType, getDrive, getTransmission, hasFeatureTranslation} =
    useCatalogue();
  const {filter} = useFilter();
  const formatNumber = useFormatNumber();
  const formatPrice = useFormatPanelPrice(priceReport);
  const {metrics, isLoading, isError} = useCurrentFilterMetrics(priceReport);

  const presetEditor = usePresetEditor(presetController, {
    deleteDialogTestId: suffixTestId('deletePresetDeleteDialog', props),
  });

  // Non-null while expanded; holds the form snapshot the rows resolve presets against
  // (“what would the market look like if this preset were selected right now”).
  const [expandedBaseValues, setExpandedBaseValues] = useState<FilterFormData | null>(null);
  const isExpanded = isNotNil(expandedBaseValues);

  // Trim catalogue rows for the current registration-year range — same source the
  // preset controller uses when applying presets.
  const firstRegistrationOnYearGte = useWatch({
    control: formApi.control,
    name: 'firstRegistrationOnYearGte',
  });
  const firstRegistrationOnYearLte = useWatch({
    control: formApi.control,
    name: 'firstRegistrationOnYearLte',
  });
  const {catalog: trimLevelsCatalog} = useTrimLevelsCatalog(
    priceReport,
    firstRegistrationOnYearGte,
    firstRegistrationOnYearLte
  );

  const summaryHelpers = useMemo(
    (): FilterSummaryHelpers => ({
      getCarStyle,
      getFuelType,
      getDrive,
      getTransmission,
      formatNumber: (value) => formatNumber(value) ?? String(value),
      formatPrice,
    }),
    [getCarStyle, getFuelType, getDrive, getTransmission, formatNumber, formatPrice]
  );

  const displayedPreset = useMemo(
    () =>
      presetController.presets.find((preset) => preset.id === presetController.displayedPresetId) ??
      null,
    [presetController.presets, presetController.displayedPresetId]
  );

  // Plain position order — unlike the sidebar dropdown, the panel does not pin the
  // default preset first, so every row can be dragged to any position.
  const orderedPresets = useMemo(
    () => sortBy((preset) => preset.position, presetController.presets),
    [presetController.presets]
  );

  const [reorderRelativeFilters] = useReorderRelativeFiltersMutation();
  const sortablePresetIds = useMemo(
    () => orderedPresets.map((preset) => preset.id),
    [orderedPresets]
  );
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const {active, over} = event;
      if (isNil(over) || active.id === over.id || isNil(presetController.customerId)) {
        return;
      }
      const oldIndex = orderedPresets.findIndex((preset) => preset.id === String(active.id));
      const newIndex = orderedPresets.findIndex((preset) => preset.id === String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      reorderRelativeFilters({
        customerId: presetController.customerId,
        ...buildReorderRelativeFiltersBody(arrayMove(orderedPresets, oldIndex, newIndex)),
      })
        .unwrap()
        .catch(handleApiError);
    },
    [orderedPresets, presetController.customerId, reorderRelativeFilters]
  );

  const name = isNotNil(displayedPreset)
    ? getPresetDisplayName(displayedPreset)
    : i18n.t('entity.filterForm.savedFiltersPanel.customFilter');
  const description = useMemo(
    () => (isNil(filter) ? null : join(', ', buildFilterSummary(filter, summaryHelpers))),
    [filter, summaryHelpers]
  );

  const toggleExpanded = useCallback(() => {
    setExpandedBaseValues((current) => (isNotNil(current) ? null : formApi.getValues()));
  }, [formApi]);

  const handleSelectPreset = useCallback(
    (preset: RelativeFilterResponseBody) => {
      if (presetController.isCatalogueLoading) {
        return;
      }
      presetController.setActivePresetId(preset.id);
      presetController.applyPreset(preset.config);
      setExpandedBaseValues(null);
    },
    [presetController]
  );

  // Preset shown → open its settings; custom filter → open the create form
  // prefilled with a relative config derived from the current filter values.
  const handleGearClick = useCallback(() => {
    if (isNotNil(displayedPreset)) {
      presetEditor.openEdit(displayedPreset.id);
      return;
    }
    presetEditor.openCreate(
      buildRelativeConfigFromFormValues(formApi.getValues(), priceReport, trimLevelsCatalog)
    );
  }, [displayedPreset, presetEditor, formApi, priceReport, trimLevelsCatalog]);

  if (isError) {
    return null;
  }

  return (
    <>
      <Card data-testid={suffixTestId('savedFiltersPanel', props)}>
        <PanelGrid>
          <PresetMetricsCells
            name={name}
            nameBadge={
              isNil(displayedPreset) ? (
                <Flag
                  label={i18n.t('entity.filterForm.savedFiltersPanel.unsaved')}
                  size="small"
                  colorScheme="neutral"
                  data-testid={suffixTestId('unsavedBadge', props)}
                />
              ) : undefined
            }
            description={description}
            metrics={metrics}
            isLoading={isLoading}
            formatPrice={formatPrice}
            data-testid={suffixTestId('activeFilter', props)}
          />
          <ActionsCell>
            <Button
              variant="outlined"
              rightIcon={isExpanded ? 'navigation/expand_less' : 'navigation/expand_more'}
              title={i18n.t('entity.filterForm.savedFiltersPanel.all')}
              onClick={toggleExpanded}
              aria-label={i18n.t(
                isExpanded
                  ? 'entity.filterForm.savedFiltersPanel.collapse'
                  : 'entity.filterForm.savedFiltersPanel.expand'
              )}
              data-testid={suffixTestId('expandButton', props)}
            />
            <IconButton
              icon="action/settings"
              priority="tertiary"
              onClick={handleGearClick}
              aria-label={i18n.t(
                isNotNil(displayedPreset)
                  ? 'entity.filterForm.savedFilters.editPreset'
                  : 'entity.filterForm.savedFiltersPanel.saveFilter'
              )}
              data-testid={suffixTestId('gearButton', props)}
            />
          </ActionsCell>

          {isExpanded ? (
            <>
              <PanelGridFullWidth>
                <Separator spacing={2} />
              </PanelGridFullWidth>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={sortablePresetIds} strategy={verticalListSortingStrategy}>
                  {orderedPresets.map((preset) => (
                    <PresetMetricsRow
                      key={preset.id}
                      preset={preset}
                      priceReport={priceReport}
                      baseFormValues={expandedBaseValues}
                      trimLevelsCatalog={trimLevelsCatalog ?? null}
                      isKnownFeature={hasFeatureTranslation}
                      summaryHelpers={summaryHelpers}
                      formatPrice={formatPrice}
                      isActive={preset.id === presetController.displayedPresetId}
                      isSortable
                      onSelect={handleSelectPreset}
                      onEdit={(rowPreset) => presetEditor.openEdit(rowPreset.id)}
                      data-testid={props['data-testid']}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <PanelGridFullWidth>
                <CreateRowSlot>
                  <Button
                    leftIcon="content/add"
                    onClick={() => {
                      setExpandedBaseValues(null);
                      presetEditor.openCreate();
                    }}
                    // The translation starts with a “+” which the icon already conveys
                    title={i18n.t('entity.filterForm.savedFilters.createNew').replace(/^\+\s*/, '')}
                    data-testid={suffixTestId('createPresetRow', props)}
                  />
                </CreateRowSlot>
              </PanelGridFullWidth>
            </>
          ) : null}
        </PanelGrid>
      </Card>

      <PresetEditorDialog
        key={presetEditor.dialogKey}
        isOpen={presetEditor.isOpen}
        initialPreset={presetEditor.dialogPreset}
        onSave={presetEditor.handleSave}
        onDelete={
          presetEditor.dialogState.mode === 'edit' ? presetEditor.handleDeletePress : undefined
        }
        onClose={presetEditor.close}
        data-testid={suffixTestId('presetEditor', props)}
      />
    </>
  );
}

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({theme}) => theme.getSize(1)};

  /* Stacked layout: actions sit next to the filter name in the first row */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-row: 1;
    grid-column: 2;
    align-self: start;
  }
`;

const CreateRowSlot = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({theme}) => `${theme.getSize(2)} 0`};
`;
